// Lade Achterstand opruimen in Mijn werk, met de markup van het prototype (BULK in design/build/prototype.js): per taak
// met een deadline van meer dan 30 dagen geleden een nieuwe vrijdag, afwerken of parkeren, en alles in één keer
// versturen. Een medewerker stelt nieuwe datums voor aan de planner (dat blijft in de app, ook zonder Project-licentie);
// de planner zet ze meteen in Planner. Afwerken en parkeren schrijven naar Planner en vragen dus een licentie.
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import { fridays } from "../lib/board";
import { endOps, parkOps } from "../lib/cleanup";
import { useTaskOps } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { ddmm, type Deadline } from "../lib/planning";
import type { PlanningData, Task, TaskOp } from "../lib/types";
import { useToast } from "../shell/Toast";

interface Props {
  items: { t: Task; d: Deadline }[];
  data: PlanningData;
  klantName: (id: string) => string;
  /** medewerker: nieuwe datums worden voorstellen */
  propose: boolean;
  write: boolean;
  onClose: () => void;
}

const plural = (n: number, one: string, many: string) => n + " " + (n === 1 ? one : many);

export function OpruimenDrawer({ items, data, klantName, propose, write, onClose }: Props) {
  const ops = useTaskOps();
  const toast = useToast();
  const qc = useQueryClient();
  const [choice, setChoice] = useState<Record<string, string | null>>({});
  const [busy, setBusy] = useState(false);
  const fr = fridays(data.today);
  const count = (n: number) => plural(n, "taak", "taken");

  const picked = items.filter((x) => choice[x.t.id]);
  const nD = picked.filter((x) => choice[x.t.id] === "done").length, nK = picked.filter((x) => choice[x.t.id] === "park").length, nP = picked.length - nD - nK;
  const parts = [nP ? plural(nP, "nieuwe datum", "nieuwe datums") : "", nD ? nD + " afwerken" : "", nK ? nK + " parkeren" : ""].filter(Boolean);

  const send = async () => {
    if (!picked.length) { toast.flash("Kies eerst per taak wat er moet gebeuren"); return; }
    setBusy(true);
    const dates = picked.filter((x) => choice[x.t.id]!.startsWith("d:")).map((x) => ({ t: x.t, end: choice[x.t.id]!.slice(2) }));
    const writes: { task: Task; ops: TaskOp[] }[] = [];
    let a = 0, failed: string[] = [];
    if (propose && dates.length) {
      const rs = await Promise.allSettled(dates.map((x) => api.propose(x.t.id, x.end)));
      failed = dates.filter((_, i) => rs[i].status === "rejected").map((x) => x.t.id);
      a = dates.length - failed.length;
      qc.invalidateQueries({ queryKey: ["proposals"] });
    } else {
      dates.forEach((x) => writes.push({ task: x.t, ops: endOps(x.t, x.end) }));
      a = dates.length;
    }
    for (const x of picked) {
      const c = choice[x.t.id];
      if (c === "done") writes.push({ task: x.t, ops: [{ kind: "progress", progress: 1 }] });
      if (c === "park") { const o = parkOps(x.t, data); if (o) writes.push({ task: x.t, ops: o }); }
    }
    const lost = failed.length;
    const msg = [
      a ? (propose ? plural(a, "voorstel", "voorstellen") + " naar de planner" : plural(a, "nieuwe datum", "nieuwe datums") + " in Planner") : "",
      nD ? nD + " afgewerkt" : "", nK ? nK + " geparkeerd" : "", lost ? plural(lost, "voorstel", "voorstellen") + " mislukt" : "",
    ].filter(Boolean).join(" · ");
    if (writes.length) ops.runMany(writes, msg); // staat meteen op het bord; mislukt er iets, dan zegt een melding dat
    else toast.flash(a ? msg : "Versturen mislukt; probeer het opnieuw", { error: lost > 0 });
    setBusy(false);
    // mislukte voorstellen blijven gekozen staan, om opnieuw te versturen
    if (!lost) onClose();
    else setChoice(Object.fromEntries(failed.map((id) => [id, choice[id]])));
  };

  return (
    <>
      <button type="button" className="p-scrim" aria-label="Sluiten" onClick={onClose} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="bt">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div>
              <p className="ex-eyebrow" style={{ margin: 0 }}>Mijn werk</p>
              <h2 id="bt">Achterstand opruimen</h2>
              <p className="p-drawer-sub">{count(items.length)} met een deadline van meer dan 30 dagen geleden. Kies per taak wat er moet gebeuren; nieuwe datums gaan {propose ? "als voorstel naar de planner." : "meteen naar Planner."}{write ? "" : " Afwerken en parkeren doet u in Planner."}</p>
            </div>
            <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={onClose}><Icon name="x" size={18} /></button>
          </div>
          <div className="p-quick" style={{ marginTop: 12 }}>
            <button type="button" className="p-tog" onClick={() => setChoice(Object.fromEntries(items.map((x) => [x.t.id, "d:" + fr[1]])))}><Icon name="cal" size={13} />Alles op vrijdag {ddmm(fr[1])}</button>
            <button type="button" className="p-tog" onClick={() => setChoice({})}>Keuzes wissen</button>
          </div>
        </div>
        <div className="p-drawer-b p-bulk">
          {items.map(({ t, d }) => {
            const ch = choice[t.id] || null;
            const opts = [...fr.map((f) => ({ key: "d:" + f, label: "vr " + ddmm(f) })),
              ...(write ? [{ key: "done", label: "Afwerken" }, ...(parkOps(t, data) ? [{ key: "park", label: "Parkeren" }] : [])] : [])];
            return (
              <div key={t.id} className="p-bulk-i">
                <b>{t.title}</b>
                <span>{klantName(t.projectId)} · deadline {ddmm(t.end)}, {d.od} dagen geleden</span>
                <div className="p-quick">
                  {opts.map((o) => <button key={o.key} type="button" className={"p-tog" + (ch === o.key ? " on" : "")} onClick={() => setChoice({ ...choice, [t.id]: ch === o.key ? null : o.key })}>{o.label}</button>)}
                </div>
              </div>
            );
          })}
          {!items.length && <p className="p-empty">Geen taken meer in de achterstand.</p>}
        </div>
        <div className="p-drawer-f">
          <span className="t-mute" style={{ fontSize: 13 }}>{parts.length ? parts.join(" · ") : "Nog niets gekozen"}</span>
          <span style={{ display: "flex", gap: 8 }}>
            <button type="button" className="ex-btn" onClick={onClose}>Annuleren</button>
            <button type="button" className={"ex-btn ex-btn--primary" + (parts.length ? "" : " p-dim")} disabled={busy} onClick={send}><Icon name="check" size={15} />Versturen</button>
          </span>
        </div>
      </aside>
    </>
  );
}
